import { utcParse, utcFormat } from "d3-time-format";
import { shield } from "./common.js";

var EXAMPLE_DATETIME = new Date(1972, 3, 27, 19, 45, 5); // End of Apollo 16 mission

var CUSTOM_FORMAT_REGEXES = {
	// Regexes matching dates where the month would commonly be written with 4-letters
	// e.g. Sept 21, june 09, July 7
	"%b %d": [
		{
			regex: /^june\s(30|[12][0-9]|0?[1-9])$/i, // matches dates from June 0-30
			toDate: function (str) {
				return new Date(null, 5, str.split(/\s/)[1]);
			},
		},
		{
			regex: /^july\s(3[01]|[12][0-9]|0?[1-9])$/i, // matches dates from July 0-31
			toDate: function (str) {
				return new Date(null, 6, str.split(/\s/)[1]);
			},
		},
		{
			regex: /^sept\s(30|[12][0-9]|0?[1-9])$/i, // matches dates from September 0-30 using the 4-letter 'Sept' abbreviation
			toDate: function (str) {
				return new Date(null, 8, str.split(/\s/)[1]);
			},
		},
	],
	// Regexes matching dates where the month would commonly be written with 4-letters
	// e.g. 21 Sept, 09 june, 7 July
	"%d %b": [
		{
			regex: /^(0?[1-9]|[1-9][0-9])\sjune$/i, // matches dates from 0-99 June
			toDate: function (str) {
				return new Date(null, 5, str.split(/\s/)[0]);
			},
		},
		{
			regex: /^(0?[1-9]|[1-9][0-9])\sjuly$/i, // matches dates from 0-99 July
			toDate: function (str) {
				return new Date(null, 6, str.split(/\s/)[0]);
			},
		},
		{
			regex: /^(0?[1-9]|[1-9][0-9])\ssept$/i, // matches dates from 0-99 September using the 4-letter 'Sept' abbreviation
			toDate: function (str) {
				return new Date(null, 8, str.split(/\s/)[0]);
			},
		},
	],
};

// Checks a string against datetime regexes for different strftime date formats so that
// non-standard datetime strings can still be parsed as dates by d3-time-parse
function customDatetimeParser(strftime_formats) {
	return function (str) {
		var parsed_datetime = null;
		strftime_formats.forEach(function (strftime_format) {
			var valid_datetime = str.match(strftime_format.regex);
			if (valid_datetime) parsed_datetime = strftime_format.toDate(str);
		});
		return parsed_datetime;
	};
}

function createDatetimeInterpretation(format_string, secondaryTest) {
	var parser = utcParse(format_string);
	var formatter = utcFormat(format_string);
	var test;
	if (typeof secondaryTest === "function") {
		test = shield(function (str) {
			return secondaryTest(str, parser(str) !== null);
		});
	} else {
		test = shield(function (str) {
			return parser(str) !== null;
		});
	}

	return Object.freeze({
		test: test,
		parse: shield(function (str) {
			return (
				parser(str) ||
				(CUSTOM_FORMAT_REGEXES[format_string]
					? customDatetimeParser(CUSTOM_FORMAT_REGEXES[format_string])(str)
					: null)
			);
		}),
		format: function (dt) {
			return formatter(dt);
		},
		type: "datetime",
		description: format_string,
		id: "datetime$" + format_string,
		example: formatter(EXAMPLE_DATETIME),
	});
}

var datetime_interpretations = Object.freeze([
	createDatetimeInterpretation("%Y-%m-%dT%H:%M:%S.%LZ"), // 1972-04-27T10:10:10.303Z
	createDatetimeInterpretation("%Y-%m-%d %H:%M:%S"), // 1972-04-27 07:45:05
	createDatetimeInterpretation("%Y-%m-%dT%H:%M:%S"), // 1972-04-27T07:45:05
	createDatetimeInterpretation("%Y-%m-%dT%H:%M:%SZ"), // 1972-04-27T10:10:10Z

	createDatetimeInterpretation("%d/%m/%Y", function (str, passed_primary_test) {
		// 27/04/1972
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12 && arr[2] >= 1000
		);
	}),
	createDatetimeInterpretation(
		"%d/%m/%Y %H:%M",
		function (str, passed_primary_test) {
			// 27/04/1972 19:45
			if (!passed_primary_test) return false;
			var arr = str.split(/[/ :]/).map(parseFloat);
			return (
				arr[0] > 0 &&
				arr[0] <= 31 &&
				arr[1] > 0 &&
				arr[1] <= 12 &&
				arr[2] >= 1000 &&
				arr[3] >= 0 &&
				arr[3] < 24 &&
				arr[4] >= 0 &&
				arr[4] < 60
			);
		},
	),
	createDatetimeInterpretation("%d/%m/%y", function (str, passed_primary_test) {
		// 27/04/72
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12 && !isNaN(arr[2])
		);
	}),
	createDatetimeInterpretation("%m/%d/%Y", function (str, passed_primary_test) {
		// 04/27/1972
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 12 && arr[1] > 0 && arr[1] <= 31 && arr[2] >= 1000
		);
	}),
	createDatetimeInterpretation(
		"%m/%d/%Y %H:%M",
		function (str, passed_primary_test) {
			// 04/27/1972 19:45
			if (!passed_primary_test) return false;
			var arr = str.split(/[/ :]/).map(parseFloat);
			return (
				arr[0] > 0 &&
				arr[0] <= 12 &&
				arr[1] > 0 &&
				arr[1] <= 31 &&
				arr[2] >= 1000 &&
				arr[3] >= 0 &&
				arr[3] < 24 &&
				arr[4] >= 0 &&
				arr[4] < 60
			);
		},
	),
	createDatetimeInterpretation("%m/%d/%y", function (str, passed_primary_test) {
		// 04/27/72
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 12 && arr[1] > 0 && arr[1] <= 31 && !isNaN(arr[2])
		);
	}),
	createDatetimeInterpretation("%Y/%m/%d", function (str, passed_primary_test) {
		// 1972/04/27
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return (
			arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 12 && arr[2] > 0 && arr[2] <= 31
		);
	}),

	createDatetimeInterpretation("%d-%m-%Y", function (str, passed_primary_test) {
		// 27-04-1972
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12 && arr[2] >= 1000
		);
	}),
	createDatetimeInterpretation("%d-%m-%y", function (str, passed_primary_test) {
		// 27-04-72
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12 && !isNaN(arr[2])
		);
	}),

	createDatetimeInterpretation("%d.%m.%Y", function (str, passed_primary_test) {
		// 27.04.1972
		if (!passed_primary_test) return false;

		var arr = str.split(".").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12 && arr[2] >= 1000
		);
	}),

	createDatetimeInterpretation("%m.%d.%y", function (str, passed_primary_test) {
		// 04.27.72
		if (!passed_primary_test) return false;

		var arr = str.split(".").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 12 && arr[1] > 0 && arr[1] <= 31 && !isNaN(arr[2])
		);
	}),

	createDatetimeInterpretation("%m-%d-%Y", function (str, passed_primary_test) {
		// 04-27-1972
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 12 && arr[1] > 0 && arr[1] <= 31 && arr[2] >= 1000
		);
	}),
	createDatetimeInterpretation("%m-%d-%y", function (str, passed_primary_test) {
		// 04-27-72
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return (
			arr[0] > 0 && arr[0] <= 12 && arr[1] > 0 && arr[1] <= 31 && !isNaN(arr[2])
		);
	}),
	createDatetimeInterpretation("%Y-%m-%d", function (str, passed_primary_test) {
		// 1972-04-27
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return (
			arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 12 && arr[2] > 0 && arr[2] <= 31
		);
	}),
	createDatetimeInterpretation("%Y-%m", function (str, passed_primary_test) {
		// 1972-04
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 12;
	}),

	createDatetimeInterpretation("%d %b %Y", function (str, passed_primary_test) {
		// 27 Apr 1972
		if (!passed_primary_test) return false;
		var arr = str.split(" ").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 31 && arr[2] >= 1000;
	}),
	createDatetimeInterpretation("%d %B %Y", function (str, passed_primary_test) {
		// 27 April 1972
		if (!passed_primary_test) return false;
		var arr = str.split(" ").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 31 && arr[2] >= 1000;
	}),
	createDatetimeInterpretation("%d %b %y"), // 27 Apr 72
	createDatetimeInterpretation("%-d %b ’%y"), // 27 Apr ’72
	createDatetimeInterpretation("%d %B %y"), // 27 April 72
	createDatetimeInterpretation("%d-%b-%Y", function (str, passed_primary_test) {
		// 27-Apr-1972
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 31 && arr[2] >= 1000;
	}),
	createDatetimeInterpretation("%d-%B-%Y", function (str, passed_primary_test) {
		// 27-April-1972
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 31 && arr[2] >= 1000;
	}),
	createDatetimeInterpretation("%d-%b-%y"), // 27-Apr-72
	createDatetimeInterpretation("%d-%B-%y"), // 27-April-72

	createDatetimeInterpretation("%m/%Y", function (str, passed_primary_test) {
		// 04/1972
		if (!passed_primary_test) return false;
		var arr = str.split("/").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 12 && arr[1] >= 1000;
	}),
	createDatetimeInterpretation("%m/%y"), // 04/72
	createDatetimeInterpretation("%b %Y", function (str, passed_primary_test) {
		// Apr 1972
		if (!passed_primary_test) return false;
		var arr = str.split(" ").map(parseFloat);
		return arr[1] >= 1000;
	}),
	createDatetimeInterpretation("%B %Y", function (str, passed_primary_test) {
		// April 1972
		if (!passed_primary_test) return false;
		var arr = str.split(" ").map(parseFloat);
		return arr[1] >= 1000;
	}),
	createDatetimeInterpretation("%b-%y"), // Apr-72
	createDatetimeInterpretation("%b %y"), // Apr 72
	createDatetimeInterpretation("%B %y"), // April 72
	createDatetimeInterpretation("%b '%y"), // Apr '72
	createDatetimeInterpretation("%b ’%y"), // Apr ’72
	createDatetimeInterpretation("%B %-d %Y"), // April 27 1972

	createDatetimeInterpretation("%d %b", function (str, passed_primary_test) {
		if (passed_primary_test) return true;
		return !!customDatetimeParser(CUSTOM_FORMAT_REGEXES["%d %b"])(str);
	}),
	createDatetimeInterpretation("%d %B"), // 27 April
	createDatetimeInterpretation("%b %d", function (str, passed_primary_test) {
		if (passed_primary_test) return true;
		return !!customDatetimeParser(CUSTOM_FORMAT_REGEXES["%b %d"])(str);
	}),
	createDatetimeInterpretation("%B %d"), // April 27
	createDatetimeInterpretation("%d-%m", function (str, passed_primary_test) {
		// 27-04
		if (!passed_primary_test) return false;
		var arr = str.split("-").map(parseFloat);
		return arr[0] > 0 && arr[0] <= 31 && arr[1] > 0 && arr[1] <= 12;
	}),
	createDatetimeInterpretation("%m-%d"), // 04-27
	createDatetimeInterpretation("%d/%m"), // 27/04
	createDatetimeInterpretation("%m/%d"), // 04/27
	createDatetimeInterpretation("%b %d %Y"), // Apr 27 1972
	createDatetimeInterpretation("%b %d %Y, %-I.%M%p"), // Apr 27 1972, 5.30PM

	createDatetimeInterpretation("%B"), // April
	createDatetimeInterpretation("%b"), // Apr

	createDatetimeInterpretation("%X"), // 7:45:05 PM
	createDatetimeInterpretation("%I:%M %p"), // 07:45 PM
	createDatetimeInterpretation("%-I.%M%p"), // 7.45PM
	createDatetimeInterpretation("%H:%M", function (str, passed_primary_test) {
		// 19:45
		if (!passed_primary_test) return false;
		var arr = str.split(":").map(parseFloat);
		return arr[0] >= 0 && arr[0] < 24;
	}),
	createDatetimeInterpretation("%H:%M:%S"), // 19:45:05
	createDatetimeInterpretation("%M:%S"), // 45:05
	createDatetimeInterpretation("%-I%p"), // 7PM

	createDatetimeInterpretation("Q%q %Y", function (str, passed_primary_test) {
		// Q2 1972
		if (!passed_primary_test) return false;
		return str.replace(/\s/g, "").length === 6;
	}),
	createDatetimeInterpretation("%Y Q%q", function (str, passed_primary_test) {
		// 1972 Q2
		if (!passed_primary_test) return false;
		return str.replace(/\s/g, "").length === 6;
	}),
	createDatetimeInterpretation("%YM%m", function (str, passed_primary_test) {
		// 1972M04
		if (!passed_primary_test) return false;
		var arr = str.split("M").map(parseFloat);
		return arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 12;
	}),
	createDatetimeInterpretation("%YQ%q", function (str, passed_primary_test) {
		// 1972Q2
		if (!passed_primary_test) return false;
		var arr = str.split("Q").map(parseFloat);
		return arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 4;
	}),
	createDatetimeInterpretation("%YÁ%q", function (str, passed_primary_test) {
		// 1972Á2
		if (!passed_primary_test) return false;
		var arr = str.split("Á").map(parseFloat);
		return arr[0] >= 1000 && arr[1] > 0 && arr[1] <= 4;
	}),
]);

// Datetime format-only interpretations - formats that could be confused with numbers
// These are available for explicit formatting but not for auto-detection
var datetime_format_only = Object.freeze([
	createDatetimeInterpretation("%Y", function (str, passed_primary_test) {
		// 1972 - could be confused with a plain number
		if (!passed_primary_test) return false;
		var val = parseFloat(str);
		return val > 1499 && val < 2200;
	}),
	createDatetimeInterpretation("%Y%m", function (str, passed_primary_test) {
		// 197204 - could be confused with a 6-digit number
		if (!passed_primary_test) return false;
		var year = parseFloat(str.slice(0, 4));
		var month = parseFloat(str.slice(4));
		return year > 1000 && month > 0 && month <= 12;
	}),
]);

export { datetime_interpretations, datetime_format_only };
